import LegalLayout, { LegalSection } from "../components/LegalLayout";
import { BRAND } from "../data/content";
import { useLang, useT } from "../i18n/LanguageContext";

export default function TermsOfService() {
  const { lang } = useLang();
  const t = useT();
  const it = lang === "it";
  return (
    <LegalLayout
      title={it ? "Termini di servizio / Cosa è incluso" : "Terms of Service / What's Included"}
      subtitle={it
        ? "Il nostro modello di servizio in chiaro: forniamo esclusivamente trasporto privato con autista. Tutto il resto è organizzato e pagato direttamente dal cliente."
        : "Our service model, made clear: we provide private chauffeur-driven transportation only. Everything else is arranged and paid for directly by the client."}
      seoTitle={t("seo.terms.title")}
      seoDesc={t("seo.terms.desc")}
      path="/terms"
    >
      <LegalSection>
        {it ? (
          <p>Nota bene: forniamo servizi professionali di trasporto con autista privato. Gli itinerari suggeriti
            sono incentrati sul viaggio e sull'esperienza delle destinazioni. Biglietti d'ingresso alle attrazioni,
            pasti e visite guidate a piedi non sono inclusi e restano a carico del cliente.</p>
        ) : (
          <p>Please note: We provide professional chauffeur-driven transportation services. Our suggested itineraries
            focus on travel and destination experiences. Entrance fees to attractions, meals, and guided walking tours
            are not included and remain the responsibility of the client.</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "1. Cosa forniamo" : "1. What we provide"}>
        {it ? (
          <p>Il nostro servizio è, esclusivamente, il <strong className="text-white">trasporto privato con autista</strong> a
            bordo di un veicolo Mercedes-Benz, con conducente professionista regolarmente autorizzato (NCC). Comprende
            il prelievo porta a porta, la guida, la pianificazione dei tempi e la logistica degli spostamenti lungo
            l'itinerario concordato.</p>
        ) : (
          <p>Our service is, exclusively, <strong className="text-white">private chauffeur-driven transportation</strong> in a
            Mercedes-Benz vehicle with a professional, fully licensed driver (NCC). It covers door-to-door pickup, the
            driving, timing and the logistics of moving between the stops on your agreed itinerary.</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "2. Cosa è incluso" : "2. What's included"}>
        {it ? (
          <ul className="list-none space-y-4 mt-2">
            <li className="border-l-2 border-[#C5A059]/50 pl-4">Veicolo privato Mercedes-Benz.</li>
            <li className="border-l-2 border-[#C5A059]/50 pl-4">Autista professionista che parla inglese, regolarmente autorizzato NCC.</li>
            <li className="border-l-2 border-[#C5A059]/50 pl-4">Prelievo e riconsegna porta a porta (hotel, villa, porto, aeroporto o qualsiasi indirizzo).</li>
            <li className="border-l-2 border-[#C5A059]/50 pl-4">Monitoraggio di voli e navi, attesa e adattamento dei tempi lungo il percorso.</li>
            <li className="border-l-2 border-[#C5A059]/50 pl-4">Carburante, pedaggi e assicurazione del veicolo.</li>
          </ul>
        ) : (
          <ul className="list-none space-y-4 mt-2">
            <li className="border-l-2 border-[#C5A059]/50 pl-4">A private Mercedes-Benz vehicle.</li>
            <li className="border-l-2 border-[#C5A059]/50 pl-4">A professional, English-speaking, fully licensed NCC chauffeur.</li>
            <li className="border-l-2 border-[#C5A059]/50 pl-4">Door-to-door pickup and drop-off (hotel, villa, port, airport or any address).</li>
            <li className="border-l-2 border-[#C5A059]/50 pl-4">Flight and ship tracking, waiting time and pacing along the route.</li>
            <li className="border-l-2 border-[#C5A059]/50 pl-4">Fuel, tolls and vehicle insurance.</li>
          </ul>
        )}
      </LegalSection>

      <LegalSection title={it ? "3. Cosa non è incluso (a carico del cliente)" : "3. What's not included (client's responsibility)"}>
        {it ? (
          <>
            <ul className="list-none space-y-4 mt-2">
              <li className="border-l-2 border-[#C5A059]/50 pl-4">Biglietti d'ingresso a musei, monumenti, attrazioni e siti.</li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">Pasti, degustazioni, visite in cantina e consumazioni.</li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">Guide turistiche abilitate e visite guidate a piedi.</li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">Escursioni in barca, biglietti del treno e altri servizi di terzi.</li>
            </ul>
            <p className="mt-4">Queste voci devono essere <strong className="text-white">prenotate direttamente dal cliente</strong> e
              pagate sul posto alla struttura o al fornitore. Il nostro autista può accompagnarti e attenderti, ma non
              organizza, prenota né fattura tali servizi.</p>
          </>
        ) : (
          <>
            <ul className="list-none space-y-4 mt-2">
              <li className="border-l-2 border-[#C5A059]/50 pl-4">Entrance fees to museums, monuments, attractions and sites.</li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">Meals, tastings, winery visits and drinks.</li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">Licensed tour guides and guided walking tours.</li>
              <li className="border-l-2 border-[#C5A059]/50 pl-4">Boat excursions, train tickets and other third-party services.</li>
            </ul>
            <p className="mt-4">These items must be <strong className="text-white">booked directly by the client</strong> and paid
              on site to the venue or provider. Your chauffeur may drive you there and wait for you, but does not
              arrange, book or invoice such services.</p>
          </>
        )}
      </LegalSection>

      <LegalSection title={it ? "4. Itinerari suggeriti" : "4. Suggested itineraries"}>
        {it ? (
          <p>Gli itinerari e le destinazioni descritti sul sito sono <strong className="text-white">proposte di viaggio</strong> pensate
            per ispirarti e per organizzare al meglio gli spostamenti. Ogni riferimento a cantine, ristoranti,
            degustazioni, musei o esperienze ha finalità puramente descrittiva del territorio: non costituisce un
            servizio fornito, organizzato o prenotato da noi.</p>
        ) : (
          <p>The itineraries and destinations described on this site are <strong className="text-white">travel suggestions</strong> intended
            to inspire you and to plan the driving day. Any reference to wineries, restaurants, tastings, museums or
            experiences is purely descriptive of the region and does not constitute a service provided, arranged or
            booked by us.</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "5. Durata della prenotazione e tariffe" : "5. Booking Duration & Pricing"}>
        {it ? (
          <p>Quando il cliente prenota un veicolo per una <strong className="text-white">disposizione oraria</strong> (un
            determinato numero di ore) o per un <strong className="text-white">tour di durata prestabilita</strong>, il
            prezzo concordato al momento della prenotazione si intende dovuto <strong className="text-white">per intero</strong>.
            Qualora, nel corso del servizio, il cliente decida di utilizzare un numero di ore inferiore a quello prenotato o
            di concludere il tour in anticipo, <span className="text-[#C5A059]">l'importo resta invariato</span> e viene
            comunque applicato sul totale delle ore prenotate: le ore non utilizzate non sono deducibili dal prezzo né danno
            diritto ad alcun rimborso o riduzione.</p>
        ) : (
          <p>When the client books a vehicle for a <strong className="text-white">time-at-disposal service</strong> (a set
            number of hours) or for a <strong className="text-white">tour of a fixed duration</strong>, the price agreed at
            the time of booking is payable <strong className="text-white">in full</strong>. Should the client, during the
            service, choose to use fewer hours than booked or to end the tour early, the{" "}
            <span className="text-[#C5A059]">price remains unchanged</span> and is applied to the total number of hours
            reserved: unused hours cannot be deducted from the price and do not entitle the client to any refund or
            reduction.</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "6. Ore eccedenti" : "6. Additional Hours"}>
        {it ? (
          <p>Eventuali <strong className="text-white">ore eccedenti</strong> rispetto a quelle prenotate, nei servizi di
            disposizione oraria o nei tour, saranno conteggiate <span className="text-[#C5A059]">arrotondate per eccesso</span> e
            non potranno essere frazionate. Tali ore saranno addebitate a parte, secondo le tariffe orarie in vigore al momento
            della prenotazione.</p>
        ) : (
          <p>Any <strong className="text-white">additional hours</strong> beyond those booked, whether for time-at-disposal
            services or tours, will be counted <span className="text-[#C5A059]">rounded up to the next full hour</span> and may
            not be fractioned. Such hours will be charged separately, in accordance with the hourly rates in effect at the time
            of booking.</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "7. Attesa inclusa (aeroporti e stazioni)" : "7. Included Waiting Time (Airports & Stations)"}>
        {it ? (
          <p>Per i prelievi in aeroporto o in stazione è incluso un tempo di attesa gratuito —{" "}
            <strong className="text-white">60 minuti per gli aeroporti</strong> e{" "}
            <strong className="text-white">15 minuti per le stazioni</strong> — a partire dall'orario di arrivo
            effettivo. Oltre questo periodo, in assenza di contatto da parte del passeggero, il servizio sarà
            considerato No-Show (penale del 100%), oppure soggetto alle tariffe orarie di attesa standard qualora
            venga concordato un servizio prolungato.</p>
        ) : (
          <p>For pick-ups at airports or train stations, a complimentary waiting time is included —{" "}
            <strong className="text-white">60 minutes for airports</strong> and{" "}
            <strong className="text-white">15 minutes for train stations</strong> — from the actual arrival time.
            Beyond this period, if no contact is made by the passenger, the service will be deemed a No-Show
            (100% fee), or subject to standard hourly waiting rates if extended service is mutually agreed upon.</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "8. Domande" : "8. Questions"}>
        {it ? (
          <p>Per qualsiasi chiarimento sul servizio, scrivici via email
            (<a href={`mailto:${BRAND.email}`} className="text-[#C5A059] underline underline-offset-2">{BRAND.email}</a>)
            o WhatsApp ({BRAND.whatsapp}). Saremo lieti di aiutarti a pianificare i tuoi spostamenti in Toscana.</p>
        ) : (
          <p>For any clarification about the service, contact us via email
            (<a href={`mailto:${BRAND.email}`} className="text-[#C5A059] underline underline-offset-2">{BRAND.email}</a>)
            or WhatsApp ({BRAND.whatsapp}). We'll gladly help you plan your travel across Tuscany.</p>
        )}
      </LegalSection>
    </LegalLayout>
  );
}
